<template>
<ul class="category-tiles">
  <li v-for="c of categories"
      :key="c.id"
      class="category-tiles__category-wrapper"
      :class="{ 'category-tiles__category-wrapper--wide': wideCategories.includes(c.id) }"
  >
    <router-link :to="categoriesLinks[c.id]"
                 class="category-tiles__category rounded"
                 :class="{ 'bg-primary-lighter': !hasCoverMedia(c) }"
    >
      <responsive-image v-if="hasCoverMedia(c)"
                        :media="c.media.cover"
                        :sizes="[wideCategories.includes(c.id) ? 'large' : 'medium']"
                        :alt="c.name"
                        class="rounded"
      />
      <p class='category-tiles__category-title' v-if="shouldDisplayCategoryName">{{ c.name }}</p>
    </router-link>
  </li>
</ul>
</template>

<script>
import ResponsiveImage from '@/components/images/ResponsiveImage'
import { mapGetters } from 'vuex'

export default {
  props: {
    categories: {
      type: Array,
    },

    wideCategories: {
      type: Array,
      default: () => [],
      validator: v => ['undefined', 'string'].includes(typeof v[0]),
    },
  },

  components: {
    ResponsiveImage,
  },

  created() {
    if (process.env.NODE_ENV !== 'production') {
      this.$watch('wideCategories', n => {
        for (const wideId of n) {
          const wideCategoryById = this.categories.find(c => c.id === wideId);

          if (!wideCategoryById) {
            throw new Error(`Wide category "${wideId}" was not found in provided categories list`);
          }
        }
      }, { immediate: true });
    }
  },

  computed: {
    ...mapGetters({
      categoriesSettings: 'settings/homeCategories',
    }),

    shouldDisplayCategoryName() {
      return this.categoriesSettings.display_names === 'always'
    },

    categoriesLinks() {
      const links = {};

      for (const c of this.categories) {
        links[c.id] = {
          name: 'catalog',
          params: {
            categoryId: c.short_id,
            categorySlug: c.slug,
          },
        };
      }

      return links;
    },
  },

  methods: {
    hasCoverMedia(category) {
      return !!category.media && !!category.media.cover && !!category.media.cover.id
    },
  },
}
</script>

<style lang="less">
@import "~@/assets/less/components/utilities";

@gap: theme('margin.5');
@image-aspect-ratio: 2;
@r: category-tiles;
.@{r} {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(theme('width.64'), 1fr));
  grid-auto-flow: row dense;
  gap: @gap;

  &__category-wrapper {
    @apply
      block // To override the default of `<li>` elements.
      ;
  }
  &__category {
    @apply block relative;
    .aspect-ratio-image(@image-aspect-ratio); // Be aware, wide cards will have a different aspect ratio. See below.
    > img {
      border-radius: 0.625rem;
      object-fit: cover;
      box-shadow: 0px 4px 25px rgba(51, 51, 51, 0.08), 0px 4px 8.44444px rgba(51, 51, 51, 0.04);
    }
  }
  &__category-title {
    @apply absolute bottom-0 left-0 py-2 px-5 text-dark rounded font-medium;
    background: #FBFBFB;
    margin: 0.875rem 0.9375rem;
  }
}

@screen lg {
  .category-tiles {
    &__category-wrapper {
      // If we apply this on all screens, we will ecounter the case where only one column of cards can be fit, but
      // having grid elements that span accross two columns makes the whole grid container have 2 columns, which results
      // in the second column having a width of 0.
      // So, effectively, the setting is ignored on screens narrower than this.
      // This is pretty stupid, but I haven't found a better way.
      &--wide {
        @wide-card-size-multiplier: 2;
        grid-column: auto / span @wide-card-size-multiplier;
        grid-row: auto / span @wide-card-size-multiplier;
        .@{r}__category {
          // Hacky override of the property injected by `.aspect-ratio-image`. Need this because wide and small cards
          // can't have the same aspect ratio and at the same time take up `k * n` grid cells, because gap is added to
          // height and width of larger cards.
          @added-size-because-of-gaps: calc((@wide-card-size-multiplier - 1) * @gap);
          padding-top: calc(
            1 / @image-aspect-ratio * (100% - @added-size-because-of-gaps)
            + @added-size-because-of-gaps
          );
        }
      }
    }
  }
}
</style>
